import React, { useEffect, useState } from 'react';
import { bridgeService } from '@/services/bridgeService';
import { Button } from '../ui';
import styles from '@/styles/components/deposit/DepositModal.module.css';

type BridgeToken = 'SWM' | 'WETH';
type BridgeChain = 'CUSTOM' | 'BASE';

interface BridgeModalProps {
  isOpen: boolean;
  onClose: () => void;
  token: BridgeToken;
  amount: string;
  requiredChain: BridgeChain;
}

export const BridgeModal: React.FC<BridgeModalProps> = ({ isOpen, onClose, token, amount, requiredChain }) => {
  const [step, setStep] = useState<'bridge' | 'success' | 'error'>('bridge');
  const [txHash, setTxHash] = useState<string>('');
  const [err, setErr] = useState<string>('');

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!isOpen) return;
      setStep('bridge');
      setErr('');
      setTxHash('');
      try {
        const res = await bridgeService.initiateBridgeWithChain(token, amount, requiredChain);
        if (!mounted) return;
        if (res.success) {
          setTxHash(res.transactionHash || '');
          setStep('success');
        } else {
          setErr(res.error || 'Bridge failed');
          setStep('error');
        }
      } catch (e: any) {
        if (!mounted) return;
        setErr(e?.message || 'Bridge failed');
        setStep('error');
      }
    };
    run();
    return () => { mounted = false; };
  }, [isOpen, token, amount, requiredChain]);

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>Bridge {token}</h2>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>
        <div className={styles.content}>
          {step === 'bridge' && (
            <div className={styles.stepContent}>
              <div className={styles.stepIcon}>⏳</div>
              <h3>Initiating Bridge</h3>
              <p>Confirm transaction(s) in your wallet.</p>
              <p>Amount: {amount} {token}</p>
            </div>
          )}

          {step === 'success' && (
            <div className={styles.stepContent}>
              <div className={styles.stepIcon}>✅</div>
              <h3>Bridge Initiated</h3>
              <p>Your bridge transaction has been submitted.</p>
              {txHash && (
                <div className={styles.transactionHash}>
                  <span>Tx:</span>
                  <code>{txHash}</code>
                </div>
              )}
              <Button variant="primary" onClick={onClose} className={styles.doneButton}>Done</Button>
            </div>
          )}

          {step === 'error' && (
            <div className={styles.stepContent}>
              <div className={styles.stepIcon}>⚠️</div>
              <h3>Bridge Failed</h3>
              <p>{err}</p>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="secondary" onClick={onClose}>Close</Button>
                <Button variant="primary" onClick={() => { setStep('bridge'); setErr(''); setTxHash(''); }}>Try Again</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BridgeModal;


